import { useState } from 'react'
import * as adminApi from '../../adminApi'
import { ApiError } from '../../api'
import type { DietaryRequirement, InvitedPerson } from '../../types'
import { DietaryPicker } from '../dietary/DietaryPicker'

interface PersonDietaryEditorProps {
  person: InvitedPerson
  onSaved: () => void
  onCancel: () => void
  onSessionLost: () => void
}

/**
 * Records one guest's dietary requirements from the console.
 *
 * For the replies that arrive by text or over the phone rather than through the
 * site. It reuses the guest's own picker, so what the couple enters is exactly
 * what the guest would have been able to enter.
 */
export function PersonDietaryEditor({
  person,
  onSaved,
  onCancel,
  onSessionLost,
}: PersonDietaryEditorProps) {
  const [dietary, setDietary] = useState<DietaryRequirement[]>(() => [...person.dietary])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const namePrefix = `admin-dietary-${person.person_id}`

  function toggleDietary(requirement: DietaryRequirement) {
    setDietary((current) =>
      current.includes(requirement)
        ? current.filter((r) => r !== requirement)
        : [...current, requirement],
    )
    setError(null)
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (saving) return

    setSaving(true)
    setError(null)
    try {
      await adminApi.setDietary(person.person_id, { dietary })
      onSaved()
    } catch (err) {
      if (err instanceof ApiError && err.isUnauthenticated) {
        onSessionLost()
        return
      }
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="border-claret/30 bg-parchment/60 mt-3 rounded-md border p-3"
    >
      <p className="text-ink-soft text-xs tracking-widest uppercase">{person.name}</p>

      {person.response !== 'yes' && (
        <p className="text-claret mt-2 text-xs">
          Only guests marked as coming can have requirements recorded. Set their response to yes
          first.
        </p>
      )}

      <div className="mt-3">
        <DietaryPicker
          selected={dietary}
          disabled={saving}
          namePrefix={namePrefix}
          onToggle={toggleDietary}
        />
      </div>

      {error && (
        <p role="alert" className="text-claret mt-3 text-sm">
          {error}
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="submit"
          disabled={saving || person.response !== 'yes'}
          className="bg-claret hover:bg-claret-dark rounded-md px-4 py-1.5 text-xs tracking-widest text-white uppercase transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save requirements'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="border-ink/20 hover:border-claret rounded-md border px-4 py-1.5 text-xs tracking-widest uppercase transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
